import { PlugZapIcon, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ContinueRail } from "../components/continue-rail";
import { HeroCarousel, HeroCarouselSkeleton } from "../components/hero-carousel";
import { NewEpisodesRail } from "../components/new-episodes-rail";
import { Rail } from "../components/rail";
import { RoomsRail } from "../components/rooms-rail";
import { Button } from "../components/ui/button";
import { type HealthPayload, type HomeSections, getHealth, getHome } from "../lib/api";
import { useSession } from "../lib/use-session";

/**
 * Strona główna.
 *
 * Sekcje pobiera JEDNYM żądaniem: pięć osobnych to pięć podróży i pięć momentów,
 * w których układ podskakuje. Serwer wie ponadto, co znaczy „strona główna" —
 * klient nie musi tej wiedzy powtarzać.
 *
 * Pas widoczny jest tylko wtedy, gdy ma treść. Nagłówek nad pustą listą wygląda
 * jak awaria wczytywania, a nie jak informacja, że w lustrze jeszcze nic nie ma.
 */
export function HomeScreen() {
  const [home, setHome] = useState<HomeSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const { session } = useSession();

  useEffect(() => {
    getHome()
      .then(setHome)
      .catch(() => setHome(null))
      .finally(() => setLoading(false));
    getHealth()
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  const empty = !loading && (home === null || home.hero.length + home.recent.length === 0);

  return (
    <div className="flex flex-col gap-10">
      {loading ? (
        <HeroCarouselSkeleton />
      ) : home !== null && home.hero.length > 0 ? (
        <HeroCarousel titles={home.hero} />
      ) : (
        /*
          Zastępcze wprowadzenie, dopóki lustro jest puste. Nie jest to szkielet
          wczytywania: lustro rośnie z wyszukiwań, więc u nowej instancji ten stan
          trwa do pierwszego użycia i musi tłumaczyć, co robić.
        */
        <section className="relative overflow-hidden rounded-xl border border-border bg-card p-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.18]"
            style={{
              background:
                "radial-gradient(60rem 20rem at 15% 0%, var(--color-primary), transparent 70%)," +
                "radial-gradient(40rem 18rem at 90% 100%, var(--color-accent), transparent 70%)",
            }}
          />
          <div className="relative flex flex-col gap-4">
            <p className="text-sm font-medium tracking-[0.2em] text-muted-foreground uppercase">
              Anisync
            </p>
            <h1 className="max-w-3xl bg-gradient-to-br from-foreground to-foreground/60 bg-clip-text text-3xl leading-tight font-bold text-transparent">
              Oglądaj razem, klatka w klatkę
            </h1>
            <p className="max-w-2xl text-muted-foreground">
              Materiał pobieramy na serwer i pokazujemy postęp, a odtwarzanie startuje dopiero z
              gotowego pliku. Dzięki temu wszyscy w pokoju widzą to samo, napisy da się stylować, a
              „oglądaj dalej" wie, gdzie skończyłeś.
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              <Button variant="hero" size="lg" asChild>
                <Link to="/szukaj">
                  <SearchIcon />
                  Szukaj anime
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/zrodla">
                  <PlugZapIcon />
                  Źródła i diagnostyka
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/*
        Nad „Kontynuuj oglądanie", bo odpowiada na świeższe pytanie: tamta półka
        wraca do rzeczy zaczętych, ta mówi o odcinku, który właśnie się pojawił.
        Pokazuje się tylko wtedy, gdy któreś źródło NAPRAWDĘ go ma.
      */}
      <NewEpisodesRail enabled={session?.authenticated === true} />

      <ContinueRail enabled={session?.authenticated === true} />

      {/*
        Wspólne oglądanie zaraz po „kontynuuj": pokój jest rzeczą, do której się
        WRACA, a wcześniej nie było jak — trasy listującej pokoje nie było w ogóle,
        więc adres znało się tylko dopóki karta pozostawała otwarta.
      */}
      <RoomsRail enabled={session?.authenticated === true} />

      {/* Emitowane najwyżej: to jedyna sekcja, która zmienia się co tydzień. */}
      {(loading || (home?.airing.length ?? 0) > 0) && (
        <Rail heading="Emitowane teraz" titles={home?.airing ?? []} loading={loading} />
      )}

      {(home?.season.length ?? 0) > 0 && (
        <Rail heading="W tym sezonie" titles={home?.season ?? []} />
      )}

      {(home?.topRated.length ?? 0) > 0 && (
        <Rail heading="Najwyżej oceniane" titles={home?.topRated ?? []} />
      )}

      <Rail
        heading="Ostatnio w katalogu"
        titles={home?.recent ?? []}
        loading={loading}
        emptyMessage="Katalog wypełnia się tym, czego szukasz — zacznij od wyszukiwania."
      />

      {empty && (
        <p className="text-sm text-muted-foreground">
          Katalog jest jeszcze pusty. Wyszukaj tytuł albo odśwież katalog źródła w panelu — lustro
          rośnie z tego, czego naprawdę ktoś szukał.
        </p>
      )}

      {health !== null && (
        <p className="text-sm text-muted-foreground">
          wersja {health.version} · cache mediów {health.mediaTtlDays} dni · logowanie{" "}
          {health.features.oidc ? "włączone" : "wyłączone"}
          {health.disk.freeBytes !== undefined &&
            ` · wolne ${(health.disk.freeBytes / 1024 ** 4).toFixed(1)} TB`}
        </p>
      )}
    </div>
  );
}
